
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useDashboardData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [mentorApplications, setMentorApplications] = useState<any[]>([]);
  const [coachRecommendations, setCoachRecommendations] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [trialSessions, setTrialSessions] = useState<any[]>([]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/admin/login');
          return;
        }

        // Check if user is admin
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('auth_user_id', user.id)
          .single();

        if (userError || !userData || userData.role !== 'admin') {
          toast({
            variant: "destructive",
            title: "Yetkisiz Erişim",
            description: "Bu sayfaya erişim yetkiniz bulunmamaktadır.",
          });
          navigate('/admin/login');
          return;
        }

        // If we get here, user is authenticated and is an admin
        fetchData();
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/admin/login');
      }
    };

    checkAuth();
  }, [navigate, toast]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [
        { data: contactsData, error: contactsError },
        { data: ordersData, error: ordersError },
        { data: applicationsData, error: applicationsError },
        { data: recommendationsData, error: recommendationsError },
        { data: couponsData, error: couponsError },
        { data: trialSessionsData, error: trialSessionsError }
      ] = await Promise.all([
        supabase.from('contacts').select('*').order('created_at', { ascending: false }),
        supabase.from('orders').select('*').order('created_at', { ascending: false }),
        supabase.from('mentor_applications').select('*').order('created_at', { ascending: false }),
        supabase.from('coach_recommendations').select('*').order('created_at', { ascending: false }),
        supabase.from('coupons').select('*').order('created_at', { ascending: false }),
        supabase.from('trial_sessions').select('*').order('created_at', { ascending: false })
      ]);

      if (contactsError || ordersError || applicationsError || recommendationsError || couponsError || trialSessionsError) {
        toast({
          variant: "destructive",
          title: "Hata",
          description: "Veri yüklenirken bir hata oluştu. Yetkiniz olduğundan emin olun.",
        });
        navigate('/admin/login');
        return;
      }

      setContacts(contactsData || []);
      setOrders(ordersData || []);
      setMentorApplications(applicationsData || []);
      setCoachRecommendations(recommendationsData || []);
      setCoupons(couponsData || []);
      setTrialSessions(trialSessionsData || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Veri yüklenirken bir hata oluştu.",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    contacts,
    setContacts,
    orders,
    setOrders,
    mentorApplications,
    coachRecommendations,
    setCoachRecommendations,
    coupons,
    setCoupons,
    trialSessions,
    setTrialSessions,
  };
};
